import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { PaperIconComponent } from '@ricoh-paper/angular';

import type { NavTraceRect } from './nav-trace';

/**
 * ONE COPY OF A GLYPH, drawn over the pill that was just pressed, for as long
 * as one animation lasts.
 *
 * Read `nav-trace.ts` first — it carries why this is an overlay rather than a
 * child of the pill, and why measuring is the only way in.
 *
 * Purely presentational and deliberately unaware of the press: it is handed a
 * glyph and a rectangle, and it exists for as long as its parent renders it.
 * The MOUNT is the animation trigger, which is what guarantees a single run —
 * the animation comes from a stylesheet, so re-rendering mid-flight cannot
 * restart it, and a fresh press is a fresh element rather than a replayed one.
 */
@Component({
  selector: 'im-nav-trace',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent],
  host: {
    // Decoration over a control that already announces its own state. A
    // second copy of the glyph in the accessibility tree would be a duplicate
    // of the pill's label with no meaning of its own.
    'aria-hidden': 'true',
    '[style.top.px]': 'rect().top',
    '[style.left.px]': 'rect().left',
    '[style.width.px]': 'rect().width',
    '[style.height.px]': 'rect().height',
  },
  template: `<paper-icon [name]="icon()" [size]="size()" />`,
  styleUrl: './nav-trace.component.scss',
})
export class NavTraceComponent {
  /** Registry name of the glyph to trace — the pill's own icon. */
  readonly icon = input.required<string>();

  /** Where the pill's icon is, in viewport coordinates. */
  readonly rect = input.required<NavTraceRect>();

  /** Matches `paper-view-track`'s own `[size]="15"`. */
  readonly size = input(15);
}
